import { Box } from '../../../shared_components/UI/Box'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectAccount,
  selectDesiredNetworkId,
} from '../../../modules/web3/selectors'
import { setDesiredNetworkId } from '../../../modules/web3/actions'
import useWeb3, { NETWORK_VERSION } from '../../../hooks/useWeb3'
import { POLYGON_NETWORK_ID } from '../../../hooks/useWeb3/constants'
import {
  useGetContractByNameQuery,
  useGetVaultForUserQuery,
} from '../../../modules/web3/api'
import { createContract } from '../Launches'
import { toast } from 'react-toastify'
import { selectHeaderHeight } from '../../../modules/miscUi/selectors'
import { ForceFullWidth } from '../../Home/parts'
import { MaxWidth } from '../../../shared_components/Page'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import TickUpNumber from '../../../shared_components/TickUpNumber'
import {
  Container,
  Hero,
  LevelBox,
  MultipliersContainer,
  Progress,
  ProgressBarContainer,
  RefCodeBox,
  SecondSection,
  Track,
  VaultInfoContainer,
  VaultPurchasedContainer,
  VaultsAndTokensContainer,
  VaultsPurchasedBox,
} from './styles'
import Kickbacks from './Kickbacks'
import { Tooltip } from 'react-tooltip'
import TokensEarned from './TokensEarned'
import { useHistory, useLocation } from 'react-router-dom'
import NftDrops from './NftDrops'
import { BASE } from '../../../constants'
import { ethers } from 'ethers'

const INIT_FAKE_VAULT = {
  id: '',
  referralCode: '',
  vaultsPurchased: 0,
  vaultsPurchasedByReferees: 0,
  currentLevel: 1,
  leftToNextLevel: 20,
  pctProgressToNextLevel: 0,
  kickbacks: [],
  userWalletAddress: '',
  tokensEarned: {
    standard: 0,
    bonus: 0,
    total: 0,
    totalWeighting: 0,
  },
  isTopLevel: false,
  currentCashback: 5,
  nextCashback: 6,
  nftModifer: 1,
  nextNftModifer: 1.1,
}

const cashbackTiers = [
  { vaults: 0, kickback: 5, nftModifer: 1 },
  { vaults: 20, kickback: 6, nftModifer: 1.1 },
  { vaults: 40, kickback: 7, nftModifer: 1.2 },
  { vaults: 60, kickback: 8, nftModifer: 1.3 },
  { vaults: 80, kickback: 9, nftModifer: 1.4 },
  { vaults: 100, kickback: 10, nftModifer: 1.5 },
]

const Vault = () => {
  const headerHeight = useSelector(selectHeaderHeight)
  const account = useSelector(selectAccount)

  const { searchParams, clearAllSearchParams } = useSearchParams()

  const txHash = searchParams.get('tx_hash')

  const { baseNetwork } = useWeb3()

  const { data: vaultContractData, isLoading: isLoadingVaultContract } =
    useGetContractByNameQuery({
      name: 'VaultSaleWithBase',
      chainId: BASE.chainId,
    })

  const [isFetchingVaultFromTxHash, setIsFetchingVaultFromTxHash] =
    useState(false)

  const {
    refetch: refetchVault,
    data: vaultDataTempDebug, // todo: replace with vault data
    isLoading: isLoadingVault,
    error: vaultError,
  } = useGetVaultForUserQuery(
    {},
    {
      skip: !!txHash || !account,
      refetchOnMountOrArgChange: true,
    },
  )

  const vaultData = undefined

  const tokensEarned = vaultDataTempDebug?.vault?.tokensEarned

  const [fakeVault, setFakeVault] = useState<IVault>(INIT_FAKE_VAULT)
  const [shouldPollVault, setShouldPollVault] = useState(false)

  const hasServerVault = !!vaultData?.vault && !vaultError
  const hasVault = hasServerVault || !!fakeVault?.referralCode

  const fakeVaultIsValid = !!fakeVault?.id && !!fakeVault?.referralCode

  const vault = fakeVaultIsValid
    ? fakeVault
    : !!vaultData?.vault
    ? vaultData?.vault
    : null

  useEffect(() => {
    const shouldClearTxHash = !!txHash && !!hasServerVault && !!fakeVaultIsValid
    if (!shouldClearTxHash) return
    clearAllSearchParams()
  }, [hasServerVault, fakeVaultIsValid, txHash, clearAllSearchParams])

  useEffect(() => {
    if (!shouldPollVault) return
    const interval = setInterval(() => {
      refetchVault()
    }, 60000)
    return () => clearInterval(interval)
  }, [shouldPollVault, refetchVault])

  const loadingAnything =
    isLoadingVaultContract || isLoadingVault || isFetchingVaultFromTxHash

  const fetchFromChain = useCallback(async () => {
    if (!account) return
    if (!baseNetwork) return
    if (!vaultContractData?.contract?.abi) return
    if (!vaultContractData?.contract?.address) return
    try {
      const { abi, address } = vaultContractData.contract
      const contractInstance = createContract(
        abi as ethers.InterfaceAbi,
        address,
        baseNetwork,
        account,
      )

      const accountData = await contractInstance.accountData(account)

      const numberOfReferrals = accountData['numberOfReferrals']
      const referralCode = accountData['referralCode']
      const numberOfVaults = accountData['numberOfVaults']

      if (!referralCode) throw new Error('Number of referrals not found')
      const highestVaultsSold = cashbackTiers[cashbackTiers.length - 1].vaults

      const isTopLevel = Number(numberOfReferrals) >= highestVaultsSold

      const currentLevel = isTopLevel
        ? cashbackTiers.length - 1
        : cashbackTiers.findIndex(
            ({ vaults }) => Number(numberOfReferrals) < vaults,
          ) - 1

      const leftToNextLevel = isTopLevel
        ? 0
        : cashbackTiers[currentLevel + 1].vaults - Number(numberOfReferrals)

      const pctProgressToNextLevel = Math.floor(
        (Number(numberOfReferrals) / cashbackTiers[currentLevel + 1]?.vaults) *
          100,
      )

      setFakeVault((p) => ({
        ...p,
        id: account,
        referralCode,
        vaultsPurchased: Number(numberOfVaults),
        vaultsPurchasedByReferees: Number(numberOfReferrals),
        userWalletAddress: account,
        currentLevel: currentLevel + 1,
        currentCashback: cashbackTiers[currentLevel].kickback,
        nextCashback: cashbackTiers[currentLevel + 1]?.kickback,
        nftModifer: cashbackTiers[currentLevel].nftModifer,
        nextNftModifer: cashbackTiers[currentLevel + 1]?.nftModifer,
        leftToNextLevel,
        isTopLevel,
        pctProgressToNextLevel,
      }))
    } catch (error) {
      console.error(error)
    }
  }, [account, baseNetwork, vaultContractData])

  useEffect(() => {
    if (!tokensEarned || !fakeVaultIsValid) return
    setFakeVault((p) => ({
      ...p,
      tokensEarned,
    }))
  }, [fakeVaultIsValid, tokensEarned])

  useEffect(() => {
    if (
      !account ||
      !baseNetwork ||
      !vaultContractData?.contract?.abi ||
      !!fakeVaultIsValid
    )
      return

    fetchFromChain()
  }, [
    account,
    vault,
    fakeVaultIsValid,
    vaultData,
    baseNetwork,
    fetchFromChain,
    vaultContractData,
  ])

  return (
    <Container headerHeight={headerHeight}>
      <ForceFullWidth>
        <Hero>
          <div className="vault-hero-gradient" />
          <MaxWidth maxWidth={1400}>
            <Box dc g={64}>
              <Box dc g={16}>
                <h1>Vault Dashboard</h1>
                <span className="lead">
                  Earn daily ZAP rewards with Vaults. Hold more Vaults and refer
                  friends to earn more.
                </span>
              </Box>
              {!!loadingAnything ? (
                <Box c className="loading-container">
                  <AnimatedLoader onlyWhite size={64} />
                </Box>
              ) : (
                <>{hasVault && <VaultInfo vault={vault} />}</>
              )}
            </Box>
          </MaxWidth>
        </Hero>
        <ForceFullWidth>
          <SecondSection>
            <MaxWidth maxWidth={1400} width="100%">
              {!!hasVault && (
                <VaultsAndTokensContainer g={32} columnMobile centerMobile>
                  <VaultsPurchased vault={vault} />
                  <TokensEarned vault={vault} />
                </VaultsAndTokensContainer>
              )}
              {!!hasVault && <Kickbacks vault={vault} />}
              <NftDrops currentNftWeight={vault?.nftModifer ?? 1} />
            </MaxWidth>
          </SecondSection>
        </ForceFullWidth>
      </ForceFullWidth>
    </Container>
  )
}

export type Kickback = {
  id: string
  type: string
  owner_id: string
  project_id: number
  attachment_id: string
  meta_data: {
    amount: string
    sender: string
    tx_hash: string
    recipient: string
    percentage_of_share: string
  }
  createdAt: string
  updatedAt: string
}

export type ITokensEarned = {
  standard: number
  bonus: number
  total: number
  totalWeighting: number
}

export type IVault = {
  id: string
  referralCode: string
  vaultsPurchased: number
  vaultsPurchasedByReferees: number
  currentLevel: number
  leftToNextLevel: number
  pctProgressToNextLevel: number
  kickbacks: Kickback[]
  tokensEarned: ITokensEarned
  currentCashback: number
  nextCashback?: number
  nftModifer: number
  nextNftModifer?: number
  userWalletAddress: string
  isTopLevel: boolean
}

export const copyToClipboard = async (text: string) => {
  if (navigator.clipboard) {
    // Check if Clipboard API is available
    try {
      await navigator.clipboard.writeText(text)
    } catch (err) {
      console.error('Failed to copy!', err)
      throw new Error('Failed to copy!')
    }
  } else {
    // Fallback for older browsers
    const textField = document.createElement('textarea')
    textField.value = text
    document.body.appendChild(textField)
    textField.select()
    document.execCommand('copy')
    textField.remove()
    console.warn('Text copied to clipboard using fallback')
  }
}

function VaultInfo({ vault }: { vault: IVault }) {
  const {
    vaultsPurchasedByReferees,
    currentLevel,
    leftToNextLevel,
    pctProgressToNextLevel,
    referralCode,
    currentCashback,
    nextCashback,
    nftModifer,
    nextNftModifer,
    isTopLevel,
  } = vault

  function copyCodeToClipboard() {
    try {
      copyToClipboard(referralCode)
      toast.success('Referral code copied')
    } catch (error) {
      console.error(error)
      toast.error('Error copying referral code')
    }
  }
  return (
    <VaultInfoContainer>
      <LevelBox asb fu aw>
        <Box dc g={16} fu>
          <Box afe jsb>
            <span className="current-level">
              Lvl <TickUpNumber amount={currentLevel} isSlow />
            </span>
            {!isTopLevel && (
              <span className="next-level">
                Lvl <TickUpNumber amount={currentLevel + 1} isSlow />
              </span>
            )}
          </Box>
          <Box dc g={12}>
            <ProgressBar progress={isTopLevel ? 100 : pctProgressToNextLevel} />
            <Box ac jsb>
              <span className="small-cap yellow">
                <TickUpNumber amount={vaultsPurchasedByReferees} isSlow />{' '}
                referral
                {vaultsPurchasedByReferees !== 1 ? 's' : ''} made
              </span>
              {!isTopLevel && (
                <span className="small-cap">
                  <TickUpNumber amount={leftToNextLevel} isSlow /> remaining
                </span>
              )}
            </Box>
          </Box>
        </Box>
        <Box dc g={16} fu>
          <span className="small-cap">referral code</span>
          <RefCodeBox jc onClick={copyCodeToClipboard}>
            <span className="code yellow">{referralCode}</span>
            <img
              className="copy-icon"
              src="/img/icons/copy.svg"
              alt="copy icon"
            />
          </RefCodeBox>
        </Box>
      </LevelBox>
      <Multipliers
        currentCashback={currentCashback}
        nextCashback={nextCashback}
        nftModifer={nftModifer}
        nextNftModifer={nextNftModifer}
      />
    </VaultInfoContainer>
  )
}

function Multipliers({
  currentCashback,
  nextCashback,
  nftModifer,
  nextNftModifer,
}: {
  currentCashback: number
  nextCashback?: number
  nftModifer: number
  nextNftModifer?: number
}) {
  const rows = [
    {
      label: 'Referral Cashback Rate',
      tooltip:
        'The percentage of the vault price that you earn as a reward for referring someone to purchase a vault.',
      currentLevel: currentCashback,
      nextLevel: nextCashback,
      unit: '%',
      icon: '/img/icons/bank-note.svg',
    },
    {
      label: 'NFT Drop Weighting',
      tooltip:
        'The weighting of your entries in NFT drops. The higher the weighting, the more likely you are to win an NFT.',
      currentLevel: nftModifer,
      nextLevel: nextNftModifer,
      icon: '/img/icons/trend.svg',
    },
  ]
  return (
    <MultipliersContainer>
      {rows.map((row, i) => {
        const { label, tooltip, currentLevel, nextLevel, unit, icon } = row
        const isFirst = i === 0
        return (
          <Box
            key={i}
            g={16}
            ac
            className={'row' + (isFirst ? ' first' : '')}
            jsb
          >
            <Box ac g={16} flex={9}>
              <img src={icon} alt={label} />
              <span className="label">{label}</span>
            </Box>
            <Box ac jsb flex={6}>
              <Box ac g={16} fu aw>
                <span className="amount">
                  <TickUpNumber amount={currentLevel} />
                  {unit}
                  {nextLevel && (
                    <span className="next-level">
                      <span style={{ fontWeight: 300 }}>{' > '}</span>
                      <TickUpNumber amount={nextLevel} />
                      {unit}
                    </span>
                  )}
                </span>
              </Box>
              <Box fu aw>
                <img
                  src="/img/icons/info-circle.svg"
                  style={{ marginRight: 8 }}
                  alt="info tooltip icon"
                  data-tooltip-id={label}
                />
                <Tooltip
                  id={label}
                  place={isFirst ? 'bottom-end' : 'top-end'}
                  opacity={1}
                  style={{
                    marginRight: '8px',
                    scale: 0.5,
                    maxWidth: '310px',
                    zIndex: 1,
                  }}
                >
                  <span>{tooltip}</span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        )
      })}
    </MultipliersContainer>
  )
}

export function useSearchParams() {
  const history = useHistory()
  const location = useLocation()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )

  const clearAllSearchParams = useCallback(() => {
    history.push(location.pathname)
  }, [history, location.pathname])

  const updateSearchParams = useCallback(
    (paramName: string, value: string) => {
      searchParams.set(paramName, value)
      history.push(`${location.pathname}?${searchParams.toString()}`)
    },
    [history, location.pathname, searchParams],
  )

  return { searchParams, updateSearchParams, clearAllSearchParams }
}

function VaultsPurchased({ vault }: { vault: IVault }) {
  const { vaultsPurchased } = vault

  const { searchParams, clearAllSearchParams } = useSearchParams()
  const account = useSelector(selectAccount)
  const [mountedWithAccount] = useState(account)

  const extraVaultsPurchased = searchParams.get('extra_vaults_purchased')
  const previousVaultsHeld = searchParams.get('previous_vaults_held')

  const expectedVaults = !!extraVaultsPurchased
    ? Number(extraVaultsPurchased) + Number(previousVaultsHeld)
    : vaultsPurchased

  useEffect(() => {
    if (!expectedVaults) return
    if (expectedVaults !== vaultsPurchased) return
    clearAllSearchParams()
  }, [
    expectedVaults,
    vaultsPurchased,
    extraVaultsPurchased,
    clearAllSearchParams,
  ])

  useEffect(() => {
    if (mountedWithAccount === account) return
    clearAllSearchParams()
  }, [mountedWithAccount, account, clearAllSearchParams])

  return (
    <VaultPurchasedContainer g={32} dc fu aw>
      <Box dc g={16}>
        <h4>Vaults</h4>
        <span className="small-cap">vaults purchased</span>
      </Box>
      <VaultsPurchasedBox>
        <Box c g={16} dc>
          <span className="amount">
            <TickUpNumber amount={expectedVaults} isSlow />
          </span>
          {/*<Link to="/vault/checkout">*/}
          {/*  <ThemedButton $styleType="primary">GET MORE VAULTS</ThemedButton>*/}
          {/*</Link>*/}
        </Box>
      </VaultsPurchasedBox>
    </VaultPurchasedContainer>
  )
}

export function ProgressBar({
  progress,
  secondProgress,
}: {
  progress: number
  secondProgress?: number
}) {
  return (
    <ProgressBarContainer>
      <Track />
      {progress > 5 && progress < 95 && (
        <div
          className="psuedo-right-border"
          style={{ width: `${progress}%` }}
        />
      )}
      <Progress progress={progress} />
      {secondProgress > 5 && <Progress progress={secondProgress} isRight />}
    </ProgressBarContainer>
  )
}

export function usePolygonIfNeeded() {
  const dispatch = useDispatch()
  const desiredNetworkId = useSelector(selectDesiredNetworkId)
  useEffect(() => {
    if (desiredNetworkId === POLYGON_NETWORK_ID) return
    dispatch(setDesiredNetworkId(POLYGON_NETWORK_ID))
    return () => {}
  }, [dispatch, desiredNetworkId])
  useEffect(() => {
    return () => {
      dispatch(setDesiredNetworkId(NETWORK_VERSION))
    }
  }, [dispatch])
  return null
}

export default Vault

export async function getDecodedEventLogFromTxHashAndEventName(
  eventName: string,
  txHash: string,
  abi: ethers.InterfaceAbi,
  provider: ethers.BrowserProvider,
) {
  try {
    const receipt = await provider.getTransactionReceipt(txHash)

    if (!receipt) {
      console.error('Transaction receipt not found.')
      return
    }

    const iface = new ethers.Interface(abi)
    const eventFragment = iface.getEvent(eventName)

    if (!eventFragment) {
      console.error('Event ABI not found for the given signature.')
      return
    }

    const log = receipt.logs[0]

    if (!log) {
      console.error(
        `Event log for ${eventName} not found in transaction receipt.`,
      )
      return
    }

    // Decode the event log data
    const decodedLog = iface.decodeEventLog(eventFragment, log.data, log.topics)
    console.info('Decoded Log:', decodedLog)
    return decodedLog
  } catch (error) {
    console.error('Error fetching or decoding transaction logs:', error)
  }
}
